
import React, { useEffect, useState } from 'react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, rectSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { ProgressSpinner } from 'primereact/progressspinner';
import DashboardService from '../../services/dashboard/dashboard.service';
import oilProductionService from '../../services/oilProduction/oilProductionservice';
import posService from '../../services/pos/pos.service';
import { OrderDashboardModel } from '../../models/dashboard/order-dashboard-model';
import OpenOrderPaymentBreakdown from './open-order-payment-breakdown';
import './performanceDashboard.css';

interface RevenueData {
  todayRevenue?: number;
  todaySalesCount?: number;
  yesterdayRevenue?: number;
  yesterdaySalesCount?: number;
  mtdRevenue?: number;
  ytdRevenue?: number;
  dailyAvgRevenue?: number;
  dailyAvgSalesCount?: number;
  avgInvoiceValue?: number;
  avgOrderValue?: number;
  yesterdayCashSales?: number;
  yesterdayCreditSales?: number;
  yesterdayOnlineSales?: number;
  yesterdayRewardSales?: number;
  todayCashSales?: number;
  todayCreditSales?: number;
  todayOnlineSales?: number;
  todayRewardSales?: number;
  mtdCashSales?: number;
  mtdCreditSales?: number;
  mtdOnlineSales?: number;
  mtdRewardSales?: number;
  rewardPointAmount?: number;
}
interface CustomersData {
  totalActiveCustomers?: number;
  customerOrderedThisMonth?: number;
  customerOrderedLast6Months?: number;
}
interface ProductionData {
  mtdAvailable: number;
  mtdUtilized: number;
  capacityLoss: number;
  yesterdayAvailable: number;
  yesterdayUtilized: number;
  oilYesterdayLitres: number;
  oilMTDLitres: number;
  todayAvailable?: number;
  todayUtilized?: number;
  oilTodayLitres?: number;
}
interface InventaryData {
  rmInventoryValuePOS4: number;
  rmInventoryValuePOS3: number;
  fgInventory: number;
  fgInventoryValuePOS1: number;
  fgInventoryValuePOS2: number;
  fgInventoryValuePOS3: number;
  fgInventoryValuePOS4: number;
  fgInventoryValuePOS5: number;
  fgInventoryValuePOS6: number;
  avgSalesAmountPerMonth: number;
  rmInventoryValue: number;
  fgInventoryValue: number;
}
interface ComplaintsData {
  complaintsYesterday: number;
  openComplaintsTillDate: number;
  openComplaintsAbove7Days: number;
  copqThisMonth: number;
}
interface RMPurchaseData {
  rmPurchaseYesterday: number;
  rmPurchaseMTD: number;
  rmPurchaseYTD: number;
}
interface DashboardData {
  revenue?: any;
  customers?: CustomersData;
  orders?: OrderDashboardModel;
  production?: ProductionData;
  inventary?: InventaryData;
  complaints?: ComplaintsData;
  rmPurchase?: RMPurchaseData;
}

interface RevenueHeader{
  todayRevenue?: number;
  yesterdayRevenue?: number;
  mtdRevenue?: number;
  ytdRevenue?: number;
}

const findRevenueData = (candidate: any): RevenueData => {
  if (!candidate) return {} as RevenueData;
  const revenueKeys = ['todayRevenue', 'yesterdayRevenue', 'mtdRevenue', 'ytdRevenue', 'dailyAvgRevenue'];
  const hasDirectRevenue = revenueKeys.some((key) => candidate[key] !== undefined && candidate[key] !== null);

  if (hasDirectRevenue) {
    return {
      todayRevenue: candidate.todayRevenue as number | undefined,
      todaySalesCount: candidate.todaySalesCount as number | undefined,
      yesterdayRevenue: candidate.yesterdayRevenue as number | undefined,
      yesterdaySalesCount: candidate.yesterdaySalesCount as number | undefined,
      mtdRevenue: candidate.mtdRevenue as number | undefined,
      ytdRevenue: candidate.ytdRevenue as number | undefined,
      dailyAvgRevenue: candidate.dailyAvgRevenue as number | undefined,
      dailyAvgSalesCount: candidate.dailyAvgSalesCount as number | undefined,
      avgInvoiceValue: candidate.avgInvoiceValue as number | undefined,
      avgOrderValue: candidate.avgOrderValue as number | undefined,
      yesterdayCashSales: candidate.yesterdayCashSales as number,
      yesterdayCreditSales: candidate.yesterdayCreditSales as number,
      yesterdayOnlineSales: candidate.yesterdayOnlineSales as number,
      yesterdayRewardSales: candidate.yesterdayRewardSales as number,
      todayCashSales: candidate.todayCashSales as number,
      todayCreditSales: candidate.todayCreditSales as number,
      todayOnlineSales: candidate.todayOnlineSales as number,
      todayRewardSales: candidate.todayRewardSales as number,
      mtdCashSales: candidate.mtdCashSales as number,
      mtdCreditSales: candidate.mtdCreditSales as number,
      mtdOnlineSales: candidate.mtdOnlineSales as number,
      mtdRewardSales: candidate.mtdRewardSales as number,
      rewardPointAmount: candidate.rewardPointAmount as number
    };
  }

  for (const value of Object.values(candidate)) {
    if (value && typeof value === 'object') {
      const nested = findRevenueData(value) as any;
      const nestedHasRevenueKey = revenueKeys.some((key) => nested[key] !== undefined && nested[key] !== null);
      if (nestedHasRevenueKey) {
        return nested as RevenueData;
      }
    }
  }

  return {} as RevenueData;
};



const fmt = (num: number | undefined): string => {
  if (num === undefined || num === null) return '—';
  const roundedAbs = Math.round(Math.abs(num));
  const sign = num < 0 ? '-' : '';

  if (roundedAbs >= 10000000) return `${sign}\u20B9${(roundedAbs / 10000000).toFixed(2)}\u00A0Cr`;
  if (roundedAbs >= 100000) return `${sign}\u20B9${(roundedAbs / 100000).toFixed(2)}\u00A0L`;
  if (roundedAbs >= 1000) return `${sign}\u20B9${(roundedAbs / 1000).toFixed(2)}K`;

  return `${sign}\u20B9${roundedAbs.toLocaleString('en-IN')}`;
};

const moneyHoverTitle = (num: number | undefined): string | undefined => {
  if (num === undefined || num === null) return undefined;
  const hasDecimals = !Number.isInteger(num);
  return `\u20B9${num.toLocaleString('en-IN', {
    minimumFractionDigits: hasDecimals ? 2 : 0,
    maximumFractionDigits: 2
  })}`;
};

const MoneyValue = ({ num }: { num?: number }) => {
  const fullValue = moneyHoverTitle(num);
  const shortValue = fmt(num);
  if (!fullValue) return <>{shortValue}</>;
  return (
    <span className="pd-money-hover" tabIndex={0}>
      {shortValue}
      <span className="pd-money-tooltip">{fullValue}</span>
    </span>
  );
};

const MoneyandLabelValue = ({ num, label }: { num?: number, label?: string }) => {
  const fullValue = moneyHoverTitle(num);
  const shortValue = fmtLakhs(num);
  if (!fullValue) return <>{shortValue}</>;
  return (
    <span className="pd-money-hover" tabIndex={0}>
      {shortValue}
      <span className="pd-money-tooltip">{label + " : " + fullValue}</span>
    </span>
  );
};

const TurnoverMoneyValue = ({ num }: { num?: number }) => {
  const fullValue = moneyHoverTitle(num);
  const shortValue = fmt(num ?? 0);
  if (!fullValue) return <>{shortValue}</>;
  return (
    <span className="pd-money-hover" tabIndex={0}>
      {shortValue}
      <span className="pd-money-tooltip">{fullValue}</span>
    </span>
  );
};

interface WipSummaryData {
  yetToCreateOil: number;
  yetToCreateCake: number;
  yetToInwardOil: number;
  yetToInwardCake: number;
  totalWipOil: number;
  totalWipCake: number;
  yetToCreateOilValue: number;
  yetToCreateCakeValue: number;
  yetToInwardOilValue: number;
  yetToInwardCakeValue: number;
  totalWipOilValue: number;
  totalWipCakeValue: number;
  finalWipValue: number;
}

const emptyWipSummary = (): WipSummaryData => ({
  yetToCreateOil: 0,
  yetToCreateCake: 0,
  yetToInwardOil: 0,
  yetToInwardCake: 0,
  totalWipOil: 0,
  totalWipCake: 0,
  yetToCreateOilValue: 0,
  yetToCreateCakeValue: 0,
  yetToInwardOilValue: 0,
  yetToInwardCakeValue: 0,
  totalWipOilValue: 0,
  totalWipCakeValue: 0,
  finalWipValue: 0
});

const buildWipSummary = (resp: any[]): WipSummaryData => {
  const summary = emptyWipSummary();

  (Array.isArray(resp) ? resp : []).forEach((rm: any) => {
    (rm.byProducts || []).forEach((bp: any) => {
      const isOil = (bp.unit || '').toString().toLowerCase() === 'litre';
      const ytc = Number(bp.yetToCreateBatch || 0);
      const yti = Number(bp.yetToInward || 0);
      const tw = Number(bp.totalWipQty || 0) || (ytc + yti);
      const pendingVal = Number(bp.pendingValue || 0);
      const batchAvailVal = Number(bp.batchAvailableValue || 0);
      const totalVal = pendingVal + batchAvailVal;

      if (isOil) {
        summary.yetToCreateOil += ytc;
        summary.yetToInwardOil += yti;
        summary.totalWipOil += tw;
        summary.yetToCreateOilValue += pendingVal;
        summary.yetToInwardOilValue += batchAvailVal;
        summary.totalWipOilValue += totalVal;
      } else {
        summary.yetToCreateCake += ytc;
        summary.yetToInwardCake += yti;
        summary.totalWipCake += tw;
        summary.yetToCreateCakeValue += pendingVal;
        summary.yetToInwardCakeValue += batchAvailVal;
        summary.totalWipCakeValue += totalVal;
      }
    });
  });

  summary.finalWipValue = (summary.totalWipOilValue || 0) + (summary.totalWipCakeValue || 0);
  return summary;
};

const fmtCount = (num: number | undefined): string => {
  if (num === undefined || num === null) return '—';
  return num.toLocaleString('en-IN');
};

const fmtFullMoney = (num: number | undefined): string => {
  if (num === undefined || num === null) return '—';
  const hasDecimals = !Number.isInteger(num);
  return `₹${num.toLocaleString('en-IN', {
    minimumFractionDigits: hasDecimals ? 2 : 0,
    maximumFractionDigits: 2
  })}`;
};

const fmtLakhs = (num: number | undefined): string => {
  if (num === undefined || num === null) return '—';
  const sign = num < 0 ? '-' : '';
  return `${sign}\u20B9${(Math.abs(num) / 100000).toFixed(2)}\u00A0L`;
};

const SummaryMetric = ({
  shortLabel,
  fullLabel,
  displayValue,
  fullValue
}: {
  shortLabel: string;
  fullLabel: string;
  displayValue: string;
  fullValue: string;
}) => (
  <span className="pd-summary-metric" title={`${fullLabel}: ${fullValue}`}>
    <span className="pd-summary-label">{shortLabel}</span>
    <span className="pd-summary-value">{displayValue}</span>
  </span>
);

interface CollapsibleSectionProps {
  section: string;
  title: string;
  color: string;
  icon: string | React.ReactNode;
  children: React.ReactNode;
  summary?: React.ReactNode;
  isExpanded?: boolean;
  onToggle?: (expanded: boolean) => void;
  headerActions?: React.ReactNode;
}

const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  section,
  title,
  color,
  icon,
  children,
  summary,
  isExpanded,
  onToggle,
  headerActions
}) => {
  const [internalExpanded, setInternalExpanded] = useState(false);
  const expanded = isExpanded !== undefined ? isExpanded : internalExpanded;

  const handleToggle = () => {
    if (onToggle) {
      onToggle(!expanded);
    } else {
      setInternalExpanded(!expanded);
    }
  };

  return (
    <div className={`pd-section pd-collapsible s-${section} ${expanded ? 'pd-section-expanded' : 'pd-section-collapsed'}`}>
      <div className="pd-collapsible-header-drag-wrapper">
        <button
          className="pd-collapsible-header"
          onClick={handleToggle}
          aria-expanded={expanded}
        >
          <div className="pd-section-title">
            <div className="pd-section-icon" style={{ background: color }}>
              {typeof icon === 'string' ? <i className={icon}></i> : icon}
            </div>
            <span className="pd-section-title-text">{title}</span>
          </div>
          <div className="pd-header-right">
            {expanded && headerActions && (
              <div className="pd-header-actions" onClick={(e) => e.stopPropagation()}>
                {headerActions}
              </div>
            )}
            {!expanded && summary && <div className="pd-header-summary">{summary}</div>}
            <span className={`pd-collapse-chevron ${expanded ? 'pd-chevron-open' : ''}`}>
              <i className="pi pi-chevron-down"></i>
            </span>
          </div>
        </button>
      </div>

      <div className={`pd-collapsible-body ${expanded ? 'pd-body-open' : ''}`}>
        <div className="pd-collapsible-body-inner">
          {children}
        </div>
      </div>
    </div>
  );
};

const SortableSection: React.FC<{ id: string; children: React.ReactNode }> = ({ id, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition, zIndex: isDragging ? 100 : 1, opacity: isDragging ? 0.6 : 1, cursor: isDragging ? 'grabbing' : 'default' };
  return (
    <div ref={setNodeRef} style={style} className="pd-sortable-item">
      <div {...attributes} {...listeners} className="pd-drag-handle">
        <div className="pd-drag-indicator"><i className="pi pi-ellipsis-v"></i><i className="pi pi-ellipsis-v"></i></div>
      </div>
      {children}
    </div>
  );
};

const RevenueSection: React.FC<{ isExpanded: boolean; onToggle: (e: boolean) => void }> = ({ isExpanded, onToggle }) => {
  const [revData, setRevData] = useState<RevenueData>();
  const [headerData, setHeaderData] = useState<RevenueHeader>();
  const [isFiltering, setIsFiltering] = useState(false);
  const [fyOptions, setFyOptions] = useState<any[]>([]);
  const [selectedFY, setSelectedFY] = useState<number | null>(null);
  const [headerLoading, setHeaderLoading] = useState(true);
  const [cardLoading, setCardLoading] = useState(false);

  // Separate function to load header first
  const loadRevenueHeader = async () => {
    setHeaderLoading(true);
    try {
      const headerResponse = await DashboardService.getDashboardRevenueHeader();
      if (headerResponse) {
        setHeaderData(headerResponse);
      }
    } catch (error) {
      console.error('Error fetching revenue header:', error);
    } finally {
      setHeaderLoading(false);
    }
  };

  // Separate function to load card data
  const loadRevenueCard = async (year: number) => {
    setCardLoading(true);
    try {
      const cardData = await DashboardService.getDashboardRevenueSummary(year);
      if (cardData) {
        setRevData(findRevenueData(cardData));
      }
    } catch (error) {
      console.error('Error fetching card data:', error);
    } finally {
      setCardLoading(false);
      setIsFiltering(false);
    }
  };

  useEffect(() => {
    const generateFYOptions = () => {
      const today = new Date();
      const currentYear = today.getFullYear();

      let startYear = currentYear;

      const options = [];
      for (let i = 0; i < 3; i++) {
        const yr = startYear - i;
        options.push({
          label: `FY ${yr}-${(yr + 1).toString().slice(-2)}`,
          value: yr
        });
      }
      setFyOptions(options);
      const defaultFY = options[0].value;
      setSelectedFY(defaultFY);
      
      // Load header immediately
      loadRevenueHeader();
      // Load card data after header
      loadRevenueCard(defaultFY);
    };

    generateFYOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const data = revData;
  const todayRevenueInstruction = 'Excluding taxes and cancelled orders';
  const yesterdayRevenueInstruction = 'Excluding taxes and cancelled orders ';
  const mtdRevenueInstruction = '1. Excluding taxes and cancelled orders\n2. Total sales from the beginning of the current month to today';
  const ytdRevenueInstruction = '1. Financial year sales value\n2. Daily avg sales = total sales ÷ total no of days\n3. Avg invoice value = total sales value ÷ total no of invoices\n4. Avg order value per customer = total sales value ÷ total ordering customers';
  const dailyAverageRevenueInstruction = 'Total taxable sales amount since the start of the financial year (previous year April - Current year April).\nCalculation: (Sum of taxable amounts between April to april) divided by (days elapsed since April 1).';
  const avgInvoiceValueInstruction = 'Total taxable sales per invoice since the start of the financial year (previous year April - Current year April).\nCalculation: (Sum of taxable amounts between April to april) divided by (total number of invoices).';
  const avgOrderValueInstruction = 'Total taxable sales amount per customer since the start of the financial year (previous year April - Current year April).\nCalculation: (Sum of taxable amounts between April to april) divided by (total number of customers).';

  return (
    <CollapsibleSection
      section="revenue"
      title="1. REVENUE INSIGHTS"
      color="#16a34a"
      icon="pi pi-chart-line"
      summary={!headerLoading && headerData && (headerData.todayRevenue || headerData.yesterdayRevenue || headerData.mtdRevenue || headerData.ytdRevenue) ? (
        <div className="pd-summary-item">
          <SummaryMetric shortLabel="TD" fullLabel="Today Revenue" displayValue={fmt(headerData.todayRevenue)} fullValue={fmtFullMoney(headerData.todayRevenue)} />
          <span className="pd-summary-divider">|</span>
          <SummaryMetric shortLabel="YD" fullLabel="Yesterday Revenue" displayValue={fmt(headerData.yesterdayRevenue)} fullValue={fmtFullMoney(headerData.yesterdayRevenue)} />
          <span className="pd-summary-divider">|</span>
          <SummaryMetric shortLabel="MTD" fullLabel="Month To Date Revenue" displayValue={fmt(headerData.mtdRevenue)} fullValue={fmtFullMoney(headerData.mtdRevenue)} />
          <span className="pd-summary-divider">|</span>
          <SummaryMetric shortLabel="YTD" fullLabel="Year To Date Revenue" displayValue={fmt(headerData.ytdRevenue)} fullValue={fmtFullMoney(headerData.ytdRevenue)} />
        </div>
      ) : null}
      isExpanded={isExpanded}
      onToggle={onToggle}
    >
      {cardLoading || !revData ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}><ProgressSpinner style={{ width: '40px', height: '40px' }} strokeWidth="4" /></div>
      ) : (
        <>
          <div className="rev-row rev-row-2col">
            <div className="pd-card pd-card-has-tooltip pd-card-has-money-tooltip rev-card-compact rev-green-hero">
              <span className="pd-card-info" aria-label={todayRevenueInstruction} tabIndex={0}>i<span className="pd-card-tooltip">{todayRevenueInstruction}</span></span>
              <div className="rev-card-badge-container">
                <div className="rev-card-badge-modern">TODAY</div>
              </div>
              <div className="rev-card-label">Today's Sales</div>
              <div className="rev-card-value-lg"><MoneyValue num={revData.todayRevenue} /></div>
              <div className="rev-card-sub"><i className="pi pi-circle-fill rev-live-dot"></i> Live Sales Snapshot</div>

              <div className="rev-breakdown-mini">
                <div className="rev-combined-pill rev-pill-cash">
                  <i className="pi pi-money-bill"></i>
                  <span className="rev-pill-label">Cash</span>
                  <span className="rev-pill-value">{fmt(revData.todayCashSales)}</span>
                </div>
                <div className="rev-combined-pill rev-pill-credit">
                  <i className="pi pi-credit-card"></i>
                  <span className="rev-pill-label">Credit</span>
                  <span className="rev-pill-value">{fmt(revData.todayCreditSales)}</span>
                </div>
                <div className="rev-combined-pill rev-pill-online">
                  <i className="pi pi-globe"></i>
                  <span className="rev-pill-label">Online</span>
                  <span className="rev-pill-value">{fmt(revData.todayOnlineSales)}</span>
                </div>
                <div className="rev-combined-pill rev-pill-reward">
                  <i className="pi pi-gift"></i>
                  <span className="rev-pill-label">Rewards</span>
                  <span className="rev-pill-value">{fmt(revData.todayRewardSales)}</span>
                </div>
              </div>
            </div>
            <div className="pd-card pd-card-has-tooltip pd-card-has-money-tooltip rev-card-compact rev-green-soft">
              <span className="pd-card-info" aria-label={yesterdayRevenueInstruction} tabIndex={0}>i<span className="pd-card-tooltip">{yesterdayRevenueInstruction}</span></span>
              <div className="rev-card-badge-container">
                <div className="rev-card-badge-modern rev-badge-yesterday">YESTERDAY</div>
              </div>
              <div className="rev-card-label">Yesterday's Sales</div>
              <div className="rev-card-value-lg rev-value-muted"><MoneyValue num={revData.yesterdayRevenue} /></div>
              <div className="rev-breakdown-mini">
                <div className="rev-combined-pill rev-pill-cash">
                  <i className="pi pi-money-bill"></i>
                  <span className="rev-pill-label">Cash</span>
                  <span className="rev-pill-value">{fmt(revData.yesterdayCashSales)}</span>
                </div>
                <div className="rev-combined-pill rev-pill-credit">
                  <i className="pi pi-credit-card"></i>
                  <span className="rev-pill-label">Credit</span>
                  <span className="rev-pill-value">{fmt(revData.yesterdayCreditSales)}</span>
                </div>
                <div className="rev-combined-pill rev-pill-online">
                  <i className="pi pi-globe"></i>
                  <span className="rev-pill-label">Online</span>
                  <span className="rev-pill-value">{fmt(revData.yesterdayOnlineSales)}</span>
                </div>
                <div className="rev-combined-pill rev-pill-reward">
                  <i className="pi pi-gift"></i>
                  <span className="rev-pill-label">Rewards</span>
                  <span className="rev-pill-value">{fmt(revData.yesterdayRewardSales)}</span>
                </div>
              </div>
            </div>
          </div>
          <div className="rev-row rev-row-1col">
            <div className="pd-card pd-card-has-tooltip pd-card-has-money-tooltip rev-card-period rev-mtd rev-card-compact-ui">
              <span className="pd-card-info" aria-label={mtdRevenueInstruction} tabIndex={0}>i<span className="pd-card-tooltip">{mtdRevenueInstruction}</span></span>
              <div className="rev-compact-header">
                <div className="rev-header-left">
                  <div className="rev-period-label">MTD</div>
                  <div className="rev-card-label">Month To Date Sales</div>
                  <div className="rev-card-value-lg"><MoneyValue num={revData.mtdRevenue} /></div>
                </div>
                <div className="rev-breakdown-professional">
                  <div className="rev-combined-pill rev-pill-cash">
                    <i className="pi pi-money-bill"></i>
                    <span className="rev-pill-label">Cash</span>
                    <span className="rev-pill-value">{fmt(revData.mtdCashSales)}</span>
                  </div>
                  <div className="rev-combined-pill rev-pill-credit">
                    <i className="pi pi-credit-card"></i>
                    <span className="rev-pill-label">Credit</span>
                    <span className="rev-pill-value">{fmt(revData.mtdCreditSales)}</span>
                  </div>
                  <div className="rev-combined-pill rev-pill-online">
                    <i className="pi pi-globe"></i>
                    <span className="rev-pill-label">Online</span>
                    <span className="rev-pill-value">{fmt(revData.mtdOnlineSales)}</span>
                  </div>
                  <div className="rev-combined-pill rev-pill-reward">
                    <i className="pi pi-gift"></i>
                    <span className="rev-pill-label">Rewards</span>
                    <span className="rev-pill-value">{fmt(revData.mtdRewardSales)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="rev-row rev-row-1col">
            <div className="pd-card pd-card-has-tooltip pd-card-has-money-tooltip rev-card-period rev-ytd rev-card-compact-ui">
              <span className="pd-card-info" aria-label={ytdRevenueInstruction} tabIndex={0}>i<span className="pd-card-tooltip">{ytdRevenueInstruction}</span></span>
              <div className="rev-compact-header">
                <div className="rev-header-left">
                  <div className="rev-ytd-header-top">
                    <div className="rev-period-label">YTD</div>
                    <Dropdown
                      value={selectedFY}
                      options={fyOptions}
                      onChange={(e) => {
                        const year = e.value;
                        setSelectedFY(year);
                        loadRevenueHeader();
                        loadRevenueCard(year);
                      }}
                      placeholder="Select FY"
                      className="rev-fy-dropdown"
                      panelClassName="rev-fy-dropdown-panel"
                    />
                  </div>
                  <div className="rev-card-label">Financial Year To Date Sales</div>
                  <div className="rev-card-value-lg"><MoneyValue num={revData.ytdRevenue} /></div>
                </div>
                <div className="rev-stats-professional-container">
                  <div className="rev-stats-professional">
                    <div className="rev-stat-prof-item">
                      <span className="rev-stat-prof-label">Daily Avg Sales</span>
                      <span className="rev-stat-prof-value"><MoneyValue num={revData.dailyAvgRevenue} /></span>
                    </div>
                    <div className="rev-stat-prof-item">
                      <span className="rev-stat-prof-label">Avg Invoice / Mo</span>
                      <span className="rev-stat-prof-value"><MoneyValue num={revData.avgInvoiceValue} /></span>
                    </div>
                    <div className="rev-stat-prof-item">
                      <span className="rev-stat-prof-label">Avg Order / Cust</span>
                      <span className="rev-stat-prof-value"><MoneyValue num={revData.avgOrderValue} /></span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </CollapsibleSection>
  );
};

const CustomerSection: React.FC<{ isExpanded: boolean; onToggle: (e: boolean) => void }> = ({ isExpanded, onToggle }) => {
  const [custData, setCustData] = useState<CustomersData>();
  const [loading, setLoading] = useState(true);
  const totalCustomersInstruction = '';
  const ordersThisMonthInstruction = 'Current month(1st-Till Date)';
  const ordersLast6MonthsInstruction = 'Past six months from  today';

  useEffect(() => {
    fetchCustomerData(new Date().getFullYear());
  }, []);

  async function fetchCustomerData(year: number) {
    setLoading(true);
    try {
      const data = await DashboardService.getDashboardCustomerSummary();
      if (data) {
        setCustData(data);
      }
    } catch (error) {
      console.error('Error fetching YTD data:', error);
    } finally {
      setLoading(false);
    }
  }

  return (
    <CollapsibleSection
      section="customerAnalytics"
      title="2. CUSTOMER ANALYTICS"
      color="#2563eb"
      icon="pi pi-users"
      summary={custData ? (
        <div className="pd-summary-item">
          <SummaryMetric shortLabel="TOTAL CUST" fullLabel="Total Customers" displayValue={fmtCount(custData.totalActiveCustomers)} fullValue={fmtCount(custData.totalActiveCustomers)} />
          <span className="pd-summary-divider">|</span>
          <SummaryMetric shortLabel="THIS MONTH" fullLabel="Customers Ordered This Month" displayValue={fmtCount(custData.customerOrderedThisMonth)} fullValue={fmtCount(custData.customerOrderedThisMonth)} />
          <span className="pd-summary-divider">|</span>
          <SummaryMetric shortLabel="6 MONTH" fullLabel="Customers Ordered Last 6 Months" displayValue={fmtCount(custData.customerOrderedLast6Months)} fullValue={fmtCount(custData.customerOrderedLast6Months)} />
        </div>
      ) : null}
      isExpanded={isExpanded}
      onToggle={onToggle}
    >
      {loading || !custData ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}><ProgressSpinner style={{ width: '40px', height: '40px' }} strokeWidth="4" /></div>
      ) : (
        <div className="cust-grid">
          <div className="pd-card pd-card-has-tooltip cust-card-hero">
            <div className="cust-hero-icon"><i className="pi pi-users"></i></div>
            <div className="cust-hero-value">{fmtCount(custData.totalActiveCustomers)}</div>
            <div className="cust-hero-label">Total Customers</div>
          </div>
          <div className="pd-card pd-card-has-tooltip cust-card-stat cust-month">
            <span className="pd-card-info" aria-label={ordersThisMonthInstruction} tabIndex={0}>i<span className="pd-card-tooltip">{ordersThisMonthInstruction}</span></span>
            <div className="cust-stat-header"><span className="cust-stat-icon-wrap"><i className="pi pi-box"></i></span><span className="cust-stat-tag">THIS MONTH</span></div>
            <div className="cust-stat-value">{fmtCount(custData.customerOrderedThisMonth)}</div>
            <div className="cust-stat-label">Customers Ordered This Month</div>
          </div>
          <div className="pd-card pd-card-has-tooltip cust-card-stat cust-6m">
            <span className="pd-card-info" aria-label={ordersLast6MonthsInstruction} tabIndex={0}>i<span className="pd-card-tooltip">{ordersLast6MonthsInstruction}</span></span>
            <div className="cust-stat-header"><span className="cust-stat-icon-wrap"><i className="pi pi-calendar"></i></span><span className="cust-stat-tag cust-tag-6m">6 MONTHS</span></div>
            <div className="cust-stat-value">{fmtCount(custData.customerOrderedLast6Months)}</div>
            <div className="cust-stat-label">Customers ordered in last 6 months</div>
          </div>
        </div>
      )}
    </CollapsibleSection>

  );
};

const OrderStatusSection: React.FC<{ isExpanded: boolean; onToggle: (e: boolean) => void }> = ({ isExpanded, onToggle }) => {
  const [data, setData] = useState<OrderDashboardModel>();
  const [loading, setLoading] = useState(true);
  const openOrdersInstruction = '';
  const yesterdayOrdersInstruction = '';
  const todayOrdersInstruction = '';
  const mtdDeliveredInstruction = 'Current month(1st-Till Date)';
  const ytdDeliveredInstruction = 'From the First month of the current year to date(including partially delivered)';

  useEffect(() => {
    fetchOrdersData(new Date().getFullYear());
  }, []);

  async function fetchOrdersData(year: number) {
    setLoading(true);
    try {
      const resp = await DashboardService.getDashboardOrderSummary();
      if (resp) {
        setData(resp);
      }
    } catch (error) {
      console.error('Error fetching order data:', error);
    } finally {
      setLoading(false);
    }
  }

  return (
    <CollapsibleSection
      section="orderStatus"
      title="3. ORDER STATUS & FULFILLMENT"
      color="#0891b2"
      icon="pi pi-truck"
      summary={data ? (
        <div className="pd-summary-item">
          <SummaryMetric shortLabel="O" fullLabel="Open Orders (Count)" displayValue={fmtCount(data.openOrders)} fullValue={fmtCount(data.openOrders)} />
          <span className="pd-summary-divider">|</span>
          <SummaryMetric shortLabel="TD" fullLabel="Today Served" displayValue={fmtCount(data.todayOrders)} fullValue={fmtCount(data.todayOrders)} />
          <span className="pd-summary-divider">|</span>
          <SummaryMetric shortLabel="YD" fullLabel="Yesterday Served" displayValue={fmtCount(data.yesterdayOrders)} fullValue={fmtCount(data.yesterdayOrders)} />
          <span className="pd-summary-divider">|</span>
          <SummaryMetric shortLabel="MTD" fullLabel="Month To Date Orders" displayValue={fmtCount(data.mtdOrders)} fullValue={fmtCount(data.mtdOrders)} />
          <span className="pd-summary-divider">|</span>
          <SummaryMetric shortLabel="YTD" fullLabel="Year To Date Orders" displayValue={fmtCount(data.ytdOrders)} fullValue={fmtCount(data.ytdOrders)} />
        </div>
      ) : null}
      isExpanded={isExpanded}
      onToggle={onToggle}
    >
      {loading || !data ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}><ProgressSpinner style={{ width: '40px', height: '40px' }} strokeWidth="4" /></div>
      ) : (
        <>
          <div className="ord-row ord-row-2col">
            <div className="pd-card pd-card-has-tooltip pd-card-has-money-tooltip ord-card-open">
              <div className="ord-open-label"><i className="pi pi-folder-open"></i> OPEN ORDERS — 30 DAYS</div>
              <div className="ord-open-value">
                {fmtCount(data.openOrders)}
                <span style={{ fontSize: '0.85em', opacity: 0.8, fontWeight: 'normal', marginLeft: '6px' }} title={`₹${data.openOrdersValue}`}>
                  (<MoneyValue num={data.openOrdersValue} />)
                </span>
              </div>
              <div className="ord-open-sub" title="">Total Open Orders Value (last 30 Days)</div>
              <OpenOrderPaymentBreakdown data={data} />
            </div>
            <div className="pd-card pd-card-has-tooltip ord-card-served ord-served-yest">
              <div className="ord-served-num">{fmtCount(data.todayOrders)}</div>
              <div className="ord-served-label">Today's Delivered</div>
              <div className="ord-served-badge"><i className="pi pi-circle-fill"></i> TODAY</div>
            </div>
          </div>
          <div className="ord-row ord-row-3col">
            <div className="pd-card pd-card-has-tooltip ord-card-today">
              <div className="ord-today-num">{fmtCount(data.yesterdayOrders)}</div>
              <div className="ord-today-label">Yesterday's Delivered</div>
              <div className="ord-today-badge"><i className="pi pi-history"></i> YESTERDAY</div>
            </div>
            <div className="pd-card pd-card-has-tooltip ord-card-delivered">
              <span className="pd-card-info" aria-label={mtdDeliveredInstruction} tabIndex={0}>i<span className="pd-card-tooltip">{mtdDeliveredInstruction}</span></span>
              <div className="ord-del-label">MTD</div>
              <div className="ord-del-value">{fmtCount(data.mtdOrders)}</div>
              <div className="ord-del-sub">Delivered MTD</div>
            </div>
            <div className="pd-card pd-card-has-tooltip ord-card-delivered">
              <span className="pd-card-info" aria-label={ytdDeliveredInstruction} tabIndex={0}>i<span className="pd-card-tooltip">{ytdDeliveredInstruction}</span></span>
              <div className="ord-del-label ord-del-ytd-label">YTD</div>
              <div className="ord-del-value">{fmtCount(data.ytdOrders)}</div>
              <div className="ord-del-sub">Delivered YTD</div>
            </div>
          </div>
        </>
      )}
    </CollapsibleSection>
  );
};

const ProductionSection: React.FC<{ posList: any[]; isExpanded: boolean; onToggle: (e: boolean) => void }> = ({ posList, isExpanded, onToggle }) => {
  const [POsId, setPOsId] = useState<number>(0);
  const [prodData, setProdData] = useState<ProductionData>();
  const [isFiltering, setIsFiltering] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => { fetchProductionData(0); }, []);

  async function fetchProductionData(posId: number) {
    setLoading(true);
    try {
      const resp = await DashboardService.getDashboardProductionCardSummary(posId);
      if (resp) setProdData(resp);
    } catch (error) { console.error("Error loading production data:", error); }
    finally { setLoading(false); }
  }

  const handleFilter = async () => {
    setIsFiltering(true);
    try {
      const resp = await DashboardService.getDashboardProductionCardSummary(POsId);
      if (resp) setProdData(resp);
    } catch (error) { console.error("Error loading production data:", error); }
    finally { setIsFiltering(false); }
  };

  const handleReset = async () => {
    setIsFiltering(true);
    setLoading(true);
    try {
      const resp = await DashboardService.getDashboardProductionCardSummary(0);
      if (resp) {
        setProdData(resp);
        setPOsId(0);
      }
    } catch (error) { console.error("Error resetting production data:", error); }
    finally { setIsFiltering(false); setLoading(false); }
  };

  const data = prodData;

  const headerActions = (
    <div className="pd-filter-group">
      <Dropdown value={POsId} options={posList} onChange={(e) => setPOsId(e.value)} optionLabel="posCode" optionValue="posID" placeholder="Select POS" className="pd-header-dropdown" />
      <Button icon="pi pi-filter" onClick={handleFilter} className="pd-header-btn pd-btn-apply" loading={isFiltering} />
      <Button icon="pi pi-refresh" onClick={handleReset} className="pd-header-btn pd-btn-reset" loading={isFiltering} tooltip="Reset Filter" />
    </div>
  );

  return (
    <CollapsibleSection
      section="production"
      title="4. Production"
      color="#d97706"
      icon="pi pi-cog"
      summary={data ? (
        <div className="pd-summary-item">
          <SummaryMetric shortLabel="OIL PRODUCED" fullLabel="Oil Produced Summary" displayValue=":" fullValue="MTD | YT | TD" />
          <SummaryMetric shortLabel="MTD" fullLabel="Month To Date Oil Produced (Ltr)" displayValue={fmtCount(data.oilMTDLitres)} fullValue={`${fmtCount(data.oilMTDLitres)} Ltr`} />
          <span className="pd-summary-divider">|</span>
          <SummaryMetric shortLabel="YT" fullLabel="Yesterday Oil Produced (Ltr)" displayValue={fmtCount(data.oilYesterdayLitres)} fullValue={`${fmtCount(data.oilYesterdayLitres)} Ltr`} />
          <span className="pd-summary-divider">|</span>
          <SummaryMetric shortLabel="TD" fullLabel="Today Oil Produced (Ltr)" displayValue={fmtCount(data.oilTodayLitres)} fullValue={`${fmtCount(data.oilTodayLitres)} Ltr`} />
        </div>
      ) : null}
      isExpanded={isExpanded}
      onToggle={onToggle}
      headerActions={headerActions}
    >
      {loading || !data ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}><ProgressSpinner style={{ width: '40px', height: '40px' }} strokeWidth="4" /></div>
      ) : (
        <>
          <div className="prod-row prod-row-3col">
            <div className="pd-card prod-card-cap prod-avail">
              <div className="prod-cap-val">{prodData.mtdAvailable ?? '—'}</div>
              <div className="prod-cap-label">MTD Available Capacity</div>
              <div className="prod-cap-badge prod-badge-avail">CAPACITY</div>
            </div>
            <div className="pd-card prod-card-cap prod-util">
              <div className="prod-cap-val">{prodData.mtdUtilized ?? '—'}</div>
              <div className="prod-cap-label">MTD Utilized Capacity</div>
              <div className="prod-cap-badge prod-badge-util">UTILIZED</div>
            </div>
            <div className="pd-card prod-card-cap prod-loss">
              <div className="prod-cap-val prod-loss-val">{prodData.capacityLoss ?? '—'}</div>
              <div className="prod-cap-label">Capacity Loss</div>
              <div className="prod-cap-badge prod-badge-loss">LOSS</div>
            </div>
          </div>
          <div className="prod-row" style={{ marginTop: '0.75rem', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1.8fr)' }}>
            <div className="pd-card prod-card-yest prod-card-split">
              <div className="prod-card-section">
                <div className="prod-card-heading">Available</div>
                <div className="prod-card-section prod-card-section-divider">
                  <div className="prod-yest-label">Today</div>
                  <div className="prod-yest-val">{prodData.todayAvailable ?? '—'}</div>
                </div>
              </div>
              <div className="prod-card-section">
                <div className="prod-yest-label">Yesterday</div>
                <div className="prod-yest-val">{prodData.yesterdayAvailable ?? '—'}</div>
              </div>
            </div>
            <div className="pd-card prod-card-yest prod-used prod-card-split">
              <div className="prod-card-section">
                <div className="prod-card-heading">Utilized</div>
                <div className="prod-card-section prod-card-section-divider">
                  <div className="prod-yest-label">Today</div>
                  <div className="prod-yest-val prod-used-val">{prodData.todayUtilized ?? '—'}</div>
                </div>
              </div>
              <div className="prod-card-section">
                <div className="prod-yest-label">Yesterday</div>
                <div className="prod-yest-val prod-used-val">{prodData.yesterdayUtilized ?? '—'}</div>
              </div>
            </div>
            <div className="pd-card prod-card-oil prod-card-split">
              <div className="prod-card-heading">Oil Produced</div>
              <div className="prod-card-section prod-card-section-divider">
                <div className="prod-yest-meta prod-subtle-meta">MTD</div>
                <div className="prod-oil-label">Month to Date</div>
                <div className="prod-oil-val prod-oil-val-lg">{fmtCount(prodData.oilMTDLitres)} <span className="prod-oil-unit">Ltr</span></div>
              </div>
              <div className="prod-oil-grid">
                <div className="prod-card-section">
                  <div className="prod-oil-label">Today</div>
                  <div className="prod-oil-val prod-oil-val-sm">{fmtCount(prodData.oilTodayLitres)} <span className="prod-oil-unit prod-oil-unit-sm">Ltr</span></div>
                </div>
                <div className="prod-card-section">
                  <div className="prod-oil-label">Yesterday</div>
                  <div className="prod-oil-val prod-oil-val-sm">{fmtCount(prodData.oilYesterdayLitres)} <span className="prod-oil-unit prod-oil-unit-sm">Ltr</span></div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </CollapsibleSection>
  );
};

const InventorySection: React.FC<{ posList: any[]; isExpanded: boolean; onToggle: (e: boolean) => void }> = ({ posList, isExpanded, onToggle }) => {
  const [POsId, setPOsId] = useState<number>(0);
  const [invData, setInvData] = useState<InventaryData>();
  const [isFiltering, setIsFiltering] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [finalWipValue, setFinalWipValue] = useState<number>(0);
  const [krWipValue, setKrWipValue] = useState<number>(0);
  const [mgtWipValue, setMgtWipValue] = useState<number>(0);

  useEffect(() => { fetchInventoryData(); }, []);

  const data = invData;
  const rawMaterialInventoryInstruction = 'Total base price (excluding tax) of all raw materials purchased till date';
  const finishedGoodsInventoryInstruction = 'Available quantity of finished goods multiplied by their respective unit prices';
  const finalWipInstruction = 'Combined work in progress value from all oil and cake batches';
  const totalInventoryInstruction = 'Raw material + finished goods + final WIP';
  const inventoryTurnoverInstruction = 'Financial year turn over (sales/month)';

  async function fetchInventoryData() {
    setLoading(true);
    try {
      const allPos = await posService.getAllPOS();
      const krPos = allPos?.find((p: any) => p.posCode === 'POS-003' || (p.posName || '').toUpperCase().includes('KR NAGAR') || (p.posName || '').toUpperCase().includes('K R NAGAR') || (p.posName || '').toUpperCase().includes('KRN1'));
      const mgtPos = allPos?.find((p: any) => p.posCode === 'POS-004' || (p.posName || '').toUpperCase().includes('MAGADI') || (p.posName || '').toUpperCase().includes('MGT'));

      const [resp, wipResp, krResp, mgtResp] = await Promise.all([
        DashboardService.getDashboardInventoryCardSummary(),
        oilProductionService.GetWipProductionMainStock([]),
        krPos ? oilProductionService.GetWipProductionMainStock([krPos.posID]) : Promise.resolve([]),
        mgtPos ? oilProductionService.GetWipProductionMainStock([mgtPos.posID]) : Promise.resolve([])
      ]);
      if (resp) setInvData(resp);
      setFinalWipValue(buildWipSummary(Array.isArray(wipResp) ? wipResp : []).finalWipValue);
      setKrWipValue(buildWipSummary(Array.isArray(krResp) ? krResp : []).finalWipValue);
      setMgtWipValue(buildWipSummary(Array.isArray(mgtResp) ? mgtResp : []).finalWipValue);
    } catch (error) { console.error("Error loading inventory data:", error); }
    finally { setLoading(false); }
  }

  return (
    <CollapsibleSection
      section="inventory"
      title="5. Inventory"
      color="#7c3aed"
      icon="pi pi-box"
      summary={invData ? (
        <div className="pd-summary-item">
          <SummaryMetric shortLabel="RM" fullLabel="Raw Material Inventory" displayValue={fmt(invData.rmInventoryValue)} fullValue={fmtFullMoney(invData.rmInventoryValue)} />
          <span className="pd-summary-divider">|</span>
          <SummaryMetric shortLabel="FG" fullLabel="Finished Goods Inventory" displayValue={fmt(invData.fgInventoryValue)} fullValue={fmtFullMoney(invData.fgInventoryValue)} />
          <span className="pd-summary-divider">|</span>
          <SummaryMetric shortLabel="WIP" fullLabel="Final WIP Value" displayValue={fmt(finalWipValue)} fullValue={fmtFullMoney(finalWipValue)} />
          <span className="pd-summary-divider">|</span>
          <SummaryMetric
            shortLabel="TOTAL"
            fullLabel="Total Inventory Value"
            displayValue={fmt((invData.rmInventoryValue || 0) + (invData.fgInventoryValue || 0) + (finalWipValue || 0))}
            fullValue={fmtFullMoney((invData.rmInventoryValue || 0) + (invData.fgInventoryValue || 0) + (finalWipValue || 0))}
          />
        </div>
      ) : null}
      isExpanded={isExpanded}
      onToggle={onToggle}
    >
      {loading || !invData ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}><ProgressSpinner style={{ width: '40px', height: '40px' }} strokeWidth="4" /></div>
      ) : (
        <div className="inv-grid">
          <div className="pd-card pd-card-has-tooltip pd-card-has-money-tooltip inv-card inv-raw">
            <span className="pd-card-info" aria-label={rawMaterialInventoryInstruction} tabIndex={0}>i<span className="pd-card-tooltip">{rawMaterialInventoryInstruction}</span></span>
            <div className="inv-card-side inv-side-raw"></div>
            <div className="inv-card-body inv-card-body-split">
              <div className="inv-body-left">
                <div className="inv-type-tag inv-tag-raw"><i className="pi pi-box"></i> RAW MATERIAL</div>
                <div className="inv-card-label">Raw Material Inventory</div>
                <div className="inv-card-value"><MoneyValue num={invData.rmInventoryValue} /></div>
              </div>
              <div className="inv-breakdown-mini">
                <div className="inv-breakdown-mini-item">
                  <span className="inv-breakdown-mini-icon inv-icon-krnagar"><i className="pi pi-map-marker"></i></span>
                  <span className="inv-breakdown-mini-label inv-label-krnagar">KR Nagar &nbsp;<MoneyandLabelValue num={invData.rmInventoryValuePOS3} label="KR Nagar" /></span>
                </div>
                <div className="inv-breakdown-mini-item">
                  <span className="inv-breakdown-mini-icon inv-icon-mgt"><i className="pi pi-building"></i></span>
                  <span className="inv-breakdown-mini-label inv-label-mgt">MGT &nbsp;<MoneyandLabelValue num={invData.rmInventoryValuePOS4} label="MGT" /></span>
                </div>
              </div>
            </div>
          </div>
          <div className="pd-card pd-card-has-tooltip pd-card-has-money-tooltip inv-card inv-fin">
            <span className="pd-card-info" aria-label={finishedGoodsInventoryInstruction} tabIndex={0}>i<span className="pd-card-tooltip">{finishedGoodsInventoryInstruction}</span></span>
            <div className="inv-card-side inv-side-fin"></div>
            <div className="inv-card-body inv-card-body-split">
              <div className="inv-body-left">
                <div className="inv-type-tag inv-tag-fin"><i className="pi pi-tag"></i> FINISHED GOODS</div>
                <div className="inv-card-label">Finished Goods Inventory</div>
                <div className="inv-card-value"><MoneyValue num={invData.fgInventoryValue} /></div>
              </div>
              <div className="inv-breakdown-mini inv-breakdown-mini-grid">
                <div className="inv-breakdown-mini-item">
                  <span className="inv-breakdown-mini-icon inv-icon-pos1"><i className="pi pi-shop"></i></span>
                  <span className="inv-breakdown-mini-label inv-label-pos1">POS1 &nbsp;<MoneyandLabelValue num={invData.fgInventoryValuePOS1} label="POS1" /></span>
                </div>
                <div className="inv-breakdown-mini-item">
                  <span className="inv-breakdown-mini-icon inv-icon-pos2"><i className="pi pi-shop"></i></span>
                  <span className="inv-breakdown-mini-label inv-label-pos2">POS2 &nbsp;<MoneyandLabelValue num={invData.fgInventoryValuePOS2} label="POS2" /></span>
                </div>
                <div className="inv-breakdown-mini-item">
                  <span className="inv-breakdown-mini-icon inv-icon-pos3"><i className="pi pi-shop"></i></span>
                  <span className="inv-breakdown-mini-label inv-label-pos3">POS3 &nbsp;<MoneyandLabelValue num={invData.fgInventoryValuePOS3} label="POS3" /></span>
                </div>
                <div className="inv-breakdown-mini-item">
                  <span className="inv-breakdown-mini-icon inv-icon-pos4"><i className="pi pi-shop"></i></span>
                  <span className="inv-breakdown-mini-label inv-label-pos4">POS4 &nbsp;<MoneyandLabelValue num={invData.fgInventoryValuePOS4} label="POS4" /></span>
                </div>
                <div className="inv-breakdown-mini-item">
                  <span className="inv-breakdown-mini-icon inv-icon-pos5"><i className="pi pi-shop"></i></span>
                  <span className="inv-breakdown-mini-label inv-label-pos5">POS5 &nbsp;<MoneyandLabelValue num={invData.fgInventoryValuePOS5} label="POS5" /></span>
                </div>
                <div className="inv-breakdown-mini-item">
                  <span className="inv-breakdown-mini-icon inv-icon-pos6"><i className="pi pi-shop"></i></span>
                  <span className="inv-breakdown-mini-label inv-label-pos6">POS6 &nbsp;<MoneyandLabelValue num={invData.fgInventoryValuePOS6} label="POS6" /></span>
                </div>
              </div>
            </div>
          </div>
          <div className="pd-card pd-card-has-tooltip pd-card-has-money-tooltip inv-card inv-wip">
            <span className="pd-card-info" aria-label={finalWipInstruction} tabIndex={0}>i<span className="pd-card-tooltip">{finalWipInstruction}</span></span>
            <div className="inv-card-side inv-side-wip"></div>
            <div className="inv-card-body inv-card-body-split">
              <div className="inv-body-left">
                <div className="inv-type-tag inv-tag-wip"><i className="pi pi-sync"></i> FINAL WIP</div>
                <div className="inv-card-label">Final WIP Value</div>
                <div className="inv-card-value inv-wip-value"><MoneyValue num={finalWipValue} /></div>
              </div>
              <div className="inv-breakdown-mini">
                <div className="inv-breakdown-mini-item">
                  <span className="inv-breakdown-mini-icon inv-icon-krnagar"><i className="pi pi-map-marker"></i></span>
                  <span className="inv-breakdown-mini-label inv-label-krnagar">KR NAGAR &nbsp;<MoneyandLabelValue num={krWipValue} label="KRN" /></span>
                </div>
                <div className="inv-breakdown-mini-item">
                  <span className="inv-breakdown-mini-icon inv-icon-mgt"><i className="pi pi-building"></i></span>
                  <span className="inv-breakdown-mini-label inv-label-mgt">MGT &nbsp;<MoneyandLabelValue num={mgtWipValue} label="MGT" /></span>
                </div>
              </div>
            </div>
          </div>
          <div className="pd-card pd-card-has-tooltip pd-card-has-money-tooltip inv-card inv-total">
            <span className="pd-card-info" aria-label={totalInventoryInstruction} tabIndex={0}>i<span className="pd-card-tooltip">{totalInventoryInstruction}</span></span>
            <div className="inv-card-side inv-side-total"></div>
            <div className="inv-card-body">
              <div className="inv-type-tag inv-tag-total"><i className="pi pi-calculator"></i> TOTAL</div>
              <div className="inv-card-label">Total Inventory Value</div>
              <div className="inv-card-value inv-total-value"><MoneyValue num={(invData.rmInventoryValue || 0) + (invData.fgInventoryValue || 0) + (finalWipValue || 0)} /></div>
            </div>
          </div>
          <div className="pd-card pd-card-has-tooltip pd-card-has-money-tooltip inv-card inv-turn">
            <span className="pd-card-info" aria-label={inventoryTurnoverInstruction} tabIndex={0}>i<span className="pd-card-tooltip">{inventoryTurnoverInstruction}</span></span>
            <div className="inv-card-side inv-side-turn"></div>
            <div className="inv-card-body">
              <div className="inv-type-tag inv-tag-turn"><i className="pi pi-refresh"></i> TURNOVER</div>
              <div className="inv-card-label">Turnover (Sales/Month)</div>
              <div className="inv-card-value inv-turn-value"><TurnoverMoneyValue num={invData.avgSalesAmountPerMonth} /></div>
            </div>
          </div>
        </div>
      )}
    </CollapsibleSection>
  );
};

const WorkInProgressSection: React.FC<{ posList: any[]; isExpanded: boolean; onToggle: (e: boolean) => void }> = ({ posList, isExpanded, onToggle }) => {
  const [posId, setPosId] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [summaryReady, setSummaryReady] = useState<boolean>(false);
  const [wip, setWip] = useState<any>({
    yetToCreateOil: 0,
    yetToCreateCake: 0,
    yetToInwardOil: 0,
    yetToInwardCake: 0,
    totalWipOil: 0,
    totalWipCake: 0
  });

  const unitOptions = posList.map((unit: any) => {
    const code = unit.posCode || '';
    const upperName = (unit.posName || unit.address2 || unit.address || '').toUpperCase();
    let shortName = '';

    if (code === 'POS-003' || upperName.includes('K R NAGAR') || upperName.includes('KR NAGAR') || upperName.includes('KRN1')) {
      shortName = 'KRN1';
    } else if (code === 'POS-004' || upperName.includes('MAGADI') || upperName.includes('MGT')) {
      shortName = 'MGT';
    } else if (code) {
      shortName = code;
    }

    return {
      ...unit,
      unitLabel: [code, shortName].filter(Boolean).join(' - ')
    };
  });

  const headerActions = (
    <div className="pd-filter-group">
      <Dropdown
        value={posId}
        options={unitOptions}
        onChange={async (e) => {
          const nextId = e.value || 0;
          setPosId(nextId);
          await loadWip(nextId);
        }}
        optionValue="posID"
        optionLabel="unitLabel"
        placeholder="Select Unit"
        className="pd-header-dropdown"
        filter={false}
      />
      <Button icon="pi pi-refresh" onClick={async () => { setPosId(0); await loadWip(0); }} className="pd-header-btn pd-btn-reset" tooltip="Reset Filter" />
    </div>
  );

  async function loadWip(pId: number) {
    setLoading(true);
    setSummaryReady(false);
    try {
      const posIds = pId ? [pId] : [];
      const resp = await oilProductionService.GetWipProductionMainStock(posIds);
      let yetToCreateOil = 0, yetToCreateCake = 0, yetToInwardOil = 0, yetToInwardCake = 0, totalWipOil = 0, totalWipCake = 0;
      let yetToCreateOilValue = 0, yetToInwardOilValue = 0, totalWipOilValue = 0;
      let yetToCreateCakeValue = 0, yetToInwardCakeValue = 0, totalWipCakeValue = 0;
      (Array.isArray(resp) ? resp : []).forEach((rm: any) => {
        (rm.byProducts || []).forEach((bp: any) => {
          const isOil = (bp.unit || '').toString().toLowerCase() === 'litre';
          const ytc = Number(bp.yetToCreateBatch || 0);
          const yti = Number(bp.yetToInward || 0);
          const tw = Number(bp.totalWipQty || 0) || (ytc + yti);
          const pendingVal = Number(bp.pendingValue || 0);
          const batchAvailVal = Number(bp.batchAvailableValue || 0);
          const totalVal = pendingVal + batchAvailVal;
          if (isOil) {
            yetToCreateOil += ytc;
            yetToInwardOil += yti;
            totalWipOil += tw;
            yetToCreateOilValue += pendingVal;
            yetToInwardOilValue += batchAvailVal;
            totalWipOilValue += totalVal;
          } else {
            yetToCreateCake += ytc;
            yetToInwardCake += yti;
            totalWipCake += tw;
            yetToCreateCakeValue += pendingVal;
            yetToInwardCakeValue += batchAvailVal;
            totalWipCakeValue += totalVal;
          }
        });
      });
      setWip({
        yetToCreateOil,
        yetToCreateCake,
        yetToInwardOil,
        yetToInwardCake,
        totalWipOil,
        totalWipCake,
        yetToCreateOilValue,
        yetToInwardOilValue,
        totalWipOilValue,
        yetToCreateCakeValue,
        yetToInwardCakeValue,
        totalWipCakeValue
      });
    } catch (e) {
      console.error('Error loading WIP section:', e);
    } finally {
      setLoading(false);
      setSummaryReady(true);
    }
  }

  useEffect(() => { loadWip(0); }, []);

  const summary = summaryReady ? (
    <div className="pd-summary-item">
      <SummaryMetric
        shortLabel="YTB"
        fullLabel="Yet To Create Batch (₹)"
        displayValue={fmt((wip.yetToCreateOilValue || 0) + (wip.yetToCreateCakeValue || 0))}
        fullValue={fmtFullMoney((wip.yetToCreateOilValue || 0) + (wip.yetToCreateCakeValue || 0))}
      />
      <span className="pd-summary-divider">|</span>
      <SummaryMetric
        shortLabel="YTI"
        fullLabel="Yet To Inward (₹)"
        displayValue={fmt((wip.yetToInwardOilValue || 0) + (wip.yetToInwardCakeValue || 0))}
        fullValue={fmtFullMoney((wip.yetToInwardOilValue || 0) + (wip.yetToInwardCakeValue || 0))}
      />
      <span className="pd-summary-divider">|</span>
      <SummaryMetric
        shortLabel="TOTAL"
        fullLabel="Total WIP (₹)"
        displayValue={fmt(((wip.totalWipOilValue || 0) + (wip.totalWipCakeValue || 0)))}
        fullValue={fmtFullMoney(((wip.totalWipOilValue || 0) + (wip.totalWipCakeValue || 0)))}
      />
    </div>
  ) : null;

  return (
    <CollapsibleSection
      section="workInProgress"
      title="8. WORK IN PROGRESS"
      color="#6d28d9"
      icon={<i className="pi pi-chart-bar" style={{ fontSize: '1rem' }}></i>}
      summary={summary}
      isExpanded={isExpanded}
      onToggle={onToggle}
      headerActions={headerActions}
    >
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}><ProgressSpinner style={{ width: '40px', height: '40px' }} strokeWidth="4" /></div>
      ) : (
        <>
          <div className="wip-oil-row">
            <div className="pd-card wip-col-create">
              <div className="wip-label-wrap">
                <span className="wip-label">Yet to Create Batch</span>
                <span className="wip-badge">Oil</span>
              </div>
              <div className="wip-value"><span className="pd-money-hover" tabIndex={0}>{fmtLakhs(wip.yetToCreateOilValue)}<span className="pd-money-tooltip">{fmtFullMoney(wip.yetToCreateOilValue)}</span></span></div>
              <div className="wip-sub">Qty: {fmtCount(wip.yetToCreateOil)}</div>
            </div>
            <div className="pd-card wip-col-inward">
              <div className="wip-label-wrap">
                <span className="wip-label">Yet to Inward</span>
                <span className="wip-badge">Oil</span>
              </div>
              <div className="wip-value"><span className="pd-money-hover" tabIndex={0}>{fmtLakhs(wip.yetToInwardOilValue)}<span className="pd-money-tooltip">{fmtFullMoney(wip.yetToInwardOilValue)}</span></span></div>
              <div className="wip-sub">Qty: {fmtCount(wip.yetToInwardOil)}</div>
            </div>
            <div className="pd-card wip-col-total">
              <div className="wip-label-wrap">
                <span className="wip-label">Total WIP Value</span>
                <span className="wip-badge">Oil</span>
              </div>
              <div className="wip-value"><span className="pd-money-hover" tabIndex={0}>{fmtLakhs(wip.totalWipOilValue)}<span className="pd-money-tooltip">{fmtFullMoney(wip.totalWipOilValue)}</span></span></div>
              <div className="wip-sub">Qty: {fmtCount(wip.totalWipOil)}</div>
            </div>
          </div>

          <div className="wip-pash-row">
            <div className="pd-card wip-col-create">
              <div className="wip-label-wrap">
                <span className="wip-label">Yet to Create Batch</span>
                <span className="wip-badge wip-badge-alt">Pashugrasa</span>
              </div>
              <div className="wip-value"><span className="pd-money-hover" tabIndex={0}>{fmtLakhs(wip.yetToCreateCakeValue)}<span className="pd-money-tooltip">{fmtFullMoney(wip.yetToCreateCakeValue)}</span></span></div>
              <div className="wip-sub">Qty: {fmtCount(wip.yetToCreateCake)}</div>
            </div>
            <div className="pd-card wip-col-inward">
              <div className="wip-label-wrap">
                <span className="wip-label">Yet to Inward</span>
                <span className="wip-badge wip-badge-alt">Pashugrasa</span>
              </div>
              <div className="wip-value"><span className="pd-money-hover" tabIndex={0}>{fmtLakhs(wip.yetToInwardCakeValue)}<span className="pd-money-tooltip">{fmtFullMoney(wip.yetToInwardCakeValue)}</span></span></div>
              <div className="wip-sub">Qty: {fmtCount(wip.yetToInwardCake)}</div>
            </div>
            <div className="pd-card wip-col-total">
              <div className="wip-label-wrap">
                <span className="wip-label">Total WIP Value</span>
                <span className="wip-badge wip-badge-alt">Pashugrasa</span>
              </div>
              <div className="wip-value"><span className="pd-money-hover" tabIndex={0}>{fmtLakhs(wip.totalWipCakeValue)}<span className="pd-money-tooltip">{fmtFullMoney(wip.totalWipCakeValue)}</span></span></div>
              <div className="wip-sub">Qty: {fmtCount(wip.totalWipCake)}</div>
            </div>
          </div>

          <div className="wip-combined-row">
            <div className="pd-card wip-col-create wip-combined-small">
              <div className="wip-label-wrap">
                <span className="wip-label">Yet to Create Batch</span>
                <span className="wip-badge">Combined</span>
              </div>
              <div className="wip-value"><span className="pd-money-hover" tabIndex={0}>{fmtLakhs(((wip.yetToCreateOilValue || 0) + (wip.yetToCreateCakeValue || 0)))}<span className="pd-money-tooltip">{fmtFullMoney(((wip.yetToCreateOilValue || 0) + (wip.yetToCreateCakeValue || 0)))}</span></span></div>
            </div>
            <div className="pd-card wip-col-inward wip-combined-small">
              <div className="wip-label-wrap">
                <span className="wip-label">Yet to Inward</span>
                <span className="wip-badge">Combined</span>
              </div>
              <div className="wip-value"><span className="pd-money-hover" tabIndex={0}>{fmtLakhs(((wip.yetToInwardOilValue || 0) + (wip.yetToInwardCakeValue || 0)))}<span className="pd-money-tooltip">{fmtFullMoney(((wip.yetToInwardOilValue || 0) + (wip.yetToInwardCakeValue || 0)))}</span></span></div>
            </div>
            <div className="pd-card wip-combined-card">
              <div className="wip-label-wrap">
                <span className="wip-label">Total WIP Value</span>
                <span className="wip-badge">Combined</span>
              </div>
              <div className="wip-value"><span className="pd-money-hover" tabIndex={0}>{fmtLakhs(((wip.totalWipCakeValue || 0) + (wip.totalWipOilValue || 0)))}<span className="pd-money-tooltip">{fmtFullMoney(((wip.totalWipCakeValue || 0) + (wip.totalWipOilValue || 0)))}</span></span></div>
            </div>
          </div>
        </>
      )}
    </CollapsibleSection>
  );
};

const QualitySection: React.FC<{ isExpanded: boolean; onToggle: (e: boolean) => void }> = ({ isExpanded, onToggle }) => {
  const [data, setData] = useState<ComplaintsData>();
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    fetchQualityData();
  }, []);

  async function fetchQualityData() {
    setLoading(true);
    try {
      const resp = await DashboardService.getDashboardQualityCardSummary();
      if (resp) {
        setData(resp);
      }
    } catch (error) {
      console.error('Error fetching quality data:', error);
    } finally {
      setLoading(false);
    }
  }


  return (
    <CollapsibleSection
      section="quality"
      title="6. QUALITY"
      color="#dc2626"
      icon="pi pi-shield"
      summary={data ? (
        <div className="pd-summary-item">
          <SummaryMetric shortLabel="CY" fullLabel="Complaints Yesterday" displayValue={fmtCount(data.complaintsYesterday)} fullValue={fmtCount(data.complaintsYesterday)} />
          <span className="pd-summary-divider">|</span>
          <SummaryMetric shortLabel="OC" fullLabel="Open Complaints" displayValue={fmtCount(data.openComplaintsTillDate)} fullValue={fmtCount(data.openComplaintsTillDate)} />
          <span className="pd-summary-divider">|</span>
          <SummaryMetric shortLabel="O7D" fullLabel="Open Complaints Above 7 Days" displayValue={fmtCount(data.openComplaintsAbove7Days)} fullValue={fmtCount(data.openComplaintsAbove7Days)} />
          <span className="pd-summary-divider">|</span>
          <SummaryMetric shortLabel="COPQ" fullLabel="Cost Of Poor Quality (This Month)" displayValue={fmt(data.copqThisMonth)} fullValue={fmtFullMoney(data.copqThisMonth)} />
        </div>
      ) : null}
      isExpanded={isExpanded}
      onToggle={onToggle}
    >
      {loading || !data ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}><ProgressSpinner style={{ width: '40px', height: '40px' }} strokeWidth="4" /></div>
      ) : (
        <div className="qual-grid">
          <div className="pd-card qual-card qual-count-card">
            <div className="qual-count-num">{fmtCount(data.complaintsYesterday)}</div>
            <div className="qual-count-label">Complaints Yesterday</div>
            <div className="qual-badge qual-badge-warn"><i className="pi pi-exclamation-triangle"></i> YESTERDAY</div>
          </div>
          <div className="pd-card qual-card qual-open-card">
            <div className="qual-count-num qual-open-num">{fmtCount(data.openComplaintsTillDate)}</div>
            <div className="qual-count-label">Open Complaints</div>
            <div className="qual-badge qual-badge-info"><i className="pi pi-folder-open"></i> OPEN TOTAL</div>
          </div>
          <div className="pd-card qual-card qual-alert-card">
            <div className="qual-count-num qual-alert-num">{fmtCount(data.openComplaintsAbove7Days)}</div>
            <div className="qual-count-label">Open &gt; 7 Days</div>
            <div className="qual-badge qual-badge-danger"><i className="pi pi-bell"></i> OVERDUE &gt; 7 DAYS</div>
          </div>
          <div className="pd-card pd-card-has-money-tooltip qual-card qual-copq-card">
            <div className="qual-copq-label">COPQ</div>
            <div className="qual-copq-value"><MoneyValue num={data.copqThisMonth} /></div>
            <div className="qual-copq-sub">COPQ This Month</div>
          </div>
        </div>
      )}
    </CollapsibleSection>
  );
};

const PurchaseSection: React.FC<{ posList: any[]; isExpanded: boolean; onToggle: (e: boolean) => void }> = ({ posList, isExpanded, onToggle }) => {
  const [POsId, setPOsId] = useState<number>(0);
  const [purData, setPurData] = useState<RMPurchaseData>();
  const [isFiltering, setIsFiltering] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedFY, setSelectedFY] = useState<number>(new Date().getFullYear());
    const [fyOptions, setFyOptions] = useState<any[]>([]);

  useEffect(() => {
    const generateFYOptions = () => {
      const today = new Date();
      const currentMonth = today.getMonth();
      const currentYear = today.getFullYear();

      let startYear = currentYear;

      const options = [];
      for (let i = 0; i < 3; i++) {
        const yr = startYear - i;
        options.push({
          label: `FY ${yr}-${(yr + 1).toString().slice(-2)}`,
          value: yr
        });
      }
      setFyOptions(options);
      fetchPurchaseData(0, currentYear);
      setSelectedFY(currentYear);
      const defaultFY = options[0].value;
      // Fetch initial data with the current financial year parameter
      handleFYChange(defaultFY);
    };
    generateFYOptions();
  
  }, []);

  async function fetchPurchaseData(posId: number, financialYear: number) {
    setLoading(true);
    try {
      const resp = await DashboardService.getDashboardpurchaseSummary(posId, financialYear);
      if (resp) setPurData(resp);
    } catch (error) { console.error("Error loading purchase data:", error); }
    finally { setLoading(false); }
  }

  const handleFilter = async () => {
    debugger;
    setIsFiltering(true);
    try {
      const resp = await DashboardService.getDashboardpurchaseSummary(POsId, selectedFY);
      if (resp) setPurData(resp);
    } catch (error) { console.error("Error loading purchase data:", error); }
    finally { setIsFiltering(false); }
  };

  const handleReset = async () => {
    setIsFiltering(true);
    setPOsId(0);
    const currentYear = new Date().getFullYear();
    setSelectedFY(currentYear);
    try {
      const resp = await DashboardService.getDashboardpurchaseSummary(0, currentYear);
      if (resp) {
        // Set in the same format as fetchPurchaseData and handleFilter
        setPurData(resp);
      }
    } catch (error) { console.error("Error resetting purchase data:", error); }
    finally { setIsFiltering(false); }
  };

  const handleFYChange = async (year: number) => {
    setSelectedFY(year);
    setIsFiltering(true);
    setLoading(true);
    try {
       var resp = await DashboardService.getDashboardpurchaseSummary(POsId, year);
       if (resp && resp.rmPurchase)
       setPurData(resp.rmPurchase);
    } catch (error) {
      console.error('Error fetching YTD data:', error);
    } finally {
      setIsFiltering(false);
      setLoading(false);
    }
  };

  const data = purData;

  const headerActions = (
    <div className="pd-filter-group">
      <Dropdown value={POsId} options={posList} onChange={(e) => setPOsId(e.value)} optionLabel="posCode" optionValue="posID" placeholder="Select POS" className="pd-header-dropdown" />
      <Button icon="pi pi-filter" onClick={handleFilter} className="pd-header-btn pd-btn-apply" loading={isFiltering} />
      <Button icon="pi pi-refresh" onClick={handleReset} className="pd-header-btn pd-btn-reset" loading={isFiltering} tooltip="Reset Filter" />
    </div>
  );

  return (
    <CollapsibleSection
      section="purchase"
      title="7. Purchase (Excl. Tax)"
      color="#ea580c"
      icon="pi pi-shopping-bag"
      summary={purData ? (
        <div className="pd-summary-item">
          <SummaryMetric shortLabel="YS" fullLabel="Yesterday Purchase" displayValue={fmt(purData.rmPurchaseYesterday)} fullValue={fmtFullMoney(purData.rmPurchaseYesterday)} />
          <span className="pd-summary-divider">|</span>
          <SummaryMetric shortLabel="MTD" fullLabel="Month To Date Purchase" displayValue={fmt(purData.rmPurchaseMTD)} fullValue={fmtFullMoney(purData.rmPurchaseMTD)} />
          <span className="pd-summary-divider">|</span>
          <SummaryMetric shortLabel="YTD" fullLabel="Year To Date Purchase" displayValue={fmt(purData.rmPurchaseYTD)} fullValue={fmtFullMoney(purData.rmPurchaseYTD)} />
        </div>
      ) : null}
      isExpanded={isExpanded}
      onToggle={onToggle}
      headerActions={headerActions}
    >
      {loading || !purData ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}><ProgressSpinner style={{ width: '40px', height: '40px' }} strokeWidth="4" /></div>
      ) : (
        <div className="pur-grid">
          <div className="pd-card pd-card-has-money-tooltip pur-card pur-yest">
            <div className="pur-period-tag"><i className="pi pi-history"></i> YESTERDAY</div>
            <div className="pur-label">Yesterday's Purchase</div>
            <div className="pur-value"><MoneyValue num={purData.rmPurchaseYesterday} /></div>
          </div>
          <div className="pd-card pd-card-has-money-tooltip pur-card pur-mtd">
            <div className="pur-period-tag pur-tag-mtd"><i className="pi pi-calendar"></i> MTD</div>
            <div className="pur-label">Month To Date Purchase</div>
            <div className="pur-value"><MoneyValue num={purData.rmPurchaseMTD} /></div>
          </div>
          <div className="pd-card pd-card-has-money-tooltip pur-card pur-ytd">
            <div className="pur-period-tag pur-tag-ytd"><i className="pi pi-chart-line"></i> YTD</div>
            <div className="pur-ytd-header-top">
              <div className="pur-period-label">YTD</div>
              <Dropdown
                value={selectedFY}
                options={fyOptions}
                onChange={(e) => handleFYChange(e.value)}
                placeholder="Select FY"
                className="pur-fy-dropdown"
                panelClassName="pur-fy-dropdown-panel"
              />
            </div>
            <div className="pur-label">Year To Date Purchase</div>
            <div className="pur-value"><MoneyValue num={purData.rmPurchaseYTD} /></div>
          </div>
        </div>
      )}
    </CollapsibleSection>
  );
};

const PerformanceDashboard: React.FC = () => {
  debugger;
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [posList, setPosList] = useState<any[]>([]);
  const [sections, setSections] = useState<string[]>(['revenue', 'customerAnalytics', 'orderStatus', 'production', 'inventory', 'quality', 'purchase', 'workInProgress']);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
  const [allPos, setAllPos] = useState<any[]>([]);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));

  useEffect(() => {
    const fetchPosList = async () => {
      try {
        const data = await posService.getAllPOS();
        if (data) {
          const mUnits = data.filter((pos: any) => pos.isManufacturingUnit === true);
          setPosList(mUnits);
          setAllPos(data);
        }
      } catch (error) { console.error("Error loading POS list:", error); }
    };
    fetchPosList();

  }, []);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setSections((items) => {
        const oldIndex = items.indexOf(active.id as string);
        const newIndex = items.indexOf(over.id as string);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const toggleSection = (id: string, expanded: boolean) => {
    setExpandedSections(prev => ({ ...prev, [id]: expanded }));
  };

  const areAllExpanded = sections.every(id => expandedSections[id]);
  const toggleAll = () => {
    const nextState = !areAllExpanded;
    const next: Record<string, boolean> = {};
    sections.forEach(id => next[id] = nextState);
    setExpandedSections(next);
  };



  const renderSection = (id: string) => {
    const isExpanded = expandedSections[id] || false;
    const commonProps = { isExpanded, onToggle: (exp: boolean) => toggleSection(id, exp) };


    switch (id) {
      case 'revenue':
        return <SortableSection key={id} id={id}><RevenueSection isExpanded={isExpanded} onToggle={(exp) => toggleSection(id, exp)} /></SortableSection>;
      case 'customerAnalytics':
        return <SortableSection key={id} id={id}><CustomerSection isExpanded={isExpanded} onToggle={(exp) => toggleSection(id, exp)} /></SortableSection>;
      case 'orderStatus':
        return <SortableSection key={id} id={id}><OrderStatusSection isExpanded={isExpanded} onToggle={(exp) => toggleSection(id, exp)} /></SortableSection>;
      case 'production':
        return <SortableSection key={id} id={id}><ProductionSection isExpanded={isExpanded} onToggle={(exp) => toggleSection(id, exp)} posList={posList} /></SortableSection>;
      case 'inventory':
        return <SortableSection key={id} id={id}><InventorySection isExpanded={isExpanded} onToggle={(exp) => toggleSection(id, exp)} posList={posList} /></SortableSection>;
      case 'quality':
        return <SortableSection key={id} id={id}><QualitySection isExpanded={isExpanded} onToggle={(exp) => toggleSection(id, exp)} /></SortableSection>;
      case 'purchase':
        return <SortableSection key={id} id={id}><PurchaseSection isExpanded={isExpanded} onToggle={(exp) => toggleSection(id, exp)} posList={allPos} /></SortableSection>;
      case 'workInProgress':
        return <SortableSection key={id} id={id}><WorkInProgressSection isExpanded={isExpanded} onToggle={(exp) => toggleSection(id, exp)} posList={posList} /></SortableSection>;
      default:
        return null;
    }
  };

  return (
    <div className="pd-container">
      <div className="pd-header-wrapper">
        <div className="pd-header-icon"><i className="pi pi-chart-line"></i></div>
        <div style={{ flex: 1 }}>
          <h1 className="pd-title">Performance Dashboard</h1>
        </div>
        <button className={`pd-expand-all-btn ${areAllExpanded ? 'pd-btn-active' : ''}`} onClick={toggleAll} title={areAllExpanded ? 'Collapse All' : 'Expand All'}>
          <i className={`pi pi-angle-double-${areAllExpanded ? 'up' : 'down'}`}></i>
          {areAllExpanded ? 'Collapse All' : 'Expand All'}
        </button>
      </div>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={sections} strategy={rectSortingStrategy}>
          <div className="pd-sections-grid">
            <div className="pd-column">
              {sections.slice(0, 4).map((id) => renderSection(id))}
            </div>
            <div className="pd-column">
              {sections.slice(4).map((id) => renderSection(id))}
            </div>
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
};

export default PerformanceDashboard;








